import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { navForRole } from '../../config/navigation';
import Logo from '../Logo';

// Role-driven sidebar. Doctors and admins get different menus (config/navigation.js).
// Items may declare a `section` — consecutive items in the same section render
// under one header (hidden when collapsed).
const SideBar = ({ collapsed = false, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const navItems = navForRole(user.role);

  let lastSection = null;

  return (
    <div
      className={`flex flex-col bg-brand-gradient-deep text-white h-screen shrink-0 transition-all duration-200 ease-in-out ${
        collapsed ? 'w-16' : 'w-56'
      }`}
    >
      {/* Brand + collapse toggle. The toggle is ALWAYS visible so a collapsed
          sidebar can be expanded again. */}
      <div className={`flex items-center h-[60px] border-b border-white/10 ${collapsed ? 'justify-center px-0' : 'justify-between px-3'}`}>
        {!collapsed && <Logo white className="h-8 w-auto" />}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1.5 rounded-md text-white/80 hover:bg-white/10 transition-colors"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
      </div>

      {/* Role badge */}
      {!collapsed && (
        <div className="px-4 pt-3 pb-1">
          <span className="inline-block text-[0.65rem] font-semibold tracking-wider uppercase text-brand-light bg-white/10 rounded-full px-2.5 py-1">
            {user.role === 'admin' ? 'Administrator' : 'Doctor'}
          </span>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-y-auto">
        <ul className="list-none p-0 m-0">
          {navItems.map((item) => {
            const showHeader = !collapsed && item.section && item.section !== lastSection;
            if (item.section) lastSection = item.section;

            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            const ItemIcon = item.icon;

            return (
              <React.Fragment key={item.path}>
                {showHeader && (
                  <li className="px-4 pt-4 pb-1 text-[0.62rem] font-semibold tracking-[0.12em] uppercase text-white/40">
                    {item.section}
                  </li>
                )}
                <li>
                  <Link
                    to={item.path}
                    title={collapsed ? item.name : undefined}
                    className={`block py-2.5 border-l-4 transition-all duration-150 ${collapsed ? 'px-0' : 'px-4'} ${
                      isActive
                        ? 'bg-white/15 border-brand-teal text-white'
                        : 'bg-transparent border-transparent text-white/75 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <span className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`}>
                      <ItemIcon className={`h-5 w-5 shrink-0 ${isActive ? 'text-brand-teal' : 'text-white/70'}`} />
                      {!collapsed && <span className="truncate text-sm">{item.name}</span>}
                    </span>
                  </Link>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      </nav>

      {!collapsed && (
        <div className="px-4 py-3 border-t border-white/10 text-[0.7rem] text-white/50 flex items-center gap-2">
          <Logo variant="icon" white className="h-4 w-4 opacity-70" />
          OmniPrac · v0.1
        </div>
      )}
    </div>
  );
};

export default SideBar;
